import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Box, Radio, RadioGroup, FormControlLabel,
  FormControl, FormLabel, Typography, Divider, Switch, Alert,
  LinearProgress, Autocomplete
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import AchievementsService from '../../services/AchievementsService';
import { getLocations } from '../../services/LocationService';
import { getStaff } from '../../services/UserService';
import SnackbarUtils from '../../utils/SnackbarUtils';
import dayjs from 'dayjs';

const SCOPE_OPTIONS = {
  GLOBAL: 'GLOBAL',
  BRANCH: 'BRANCH',
  PERSONAL: 'PERSONAL'
};

const AssignAchievementModal = ({ open, onClose, onSave, achievement }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [progress, setProgress] = useState(0);
  const [branches, setBranches] = useState([]);
  const [staff, setStaff] = useState([]);
  const [formData, setFormData] = useState({
    scope: SCOPE_OPTIONS.GLOBAL,
    branch_id: null,
    user_id: null,
    month: dayjs().month() + 1, // Mes actual (1-12)
    year: dayjs().year(), // Año actual
    requirement_value: '',
    assign_for_year: false // Nueva opción para asignar todo el año
  });

  // Memoizar el usuario seleccionado para evitar que se restablezca
  const selectedUser = useMemo(() => {
    if (!formData.user_id || !staff.length) return null;
    return staff.find(user => {
      // El ID del usuario es el campo 'ci' (cédula de identidad)
      return user.ci === formData.user_id;
    }) || null;
  }, [formData.user_id, staff]);

  useEffect(() => {
    if (open && achievement) {
      loadBranches();
      loadStaff();
      // Pre-llenar con el valor por defecto si existe
      const defaultRequirementValue = achievement.defaultRequirementValue ?? achievement.default_requirement_value ?? achievement.requirementValue ?? achievement.requirement_value ?? achievement.targetValue ?? achievement.target_value ?? '';
      setFormData(prev => ({
        ...prev,
        scope: SCOPE_OPTIONS.GLOBAL,
        branch_id: null,
        user_id: null,
        month: dayjs().month() + 1,
        year: dayjs().year(),
        requirement_value: defaultRequirementValue,
        assign_for_year: false
      }));
    } else if (open && !achievement) {
      // Si se abre sin logro (no debería pasar, pero por si acaso)
      setFormData({
        scope: SCOPE_OPTIONS.GLOBAL,
        branch_id: null,
        user_id: null,
        month: dayjs().month() + 1,
        year: dayjs().year(),
        requirement_value: '',
        assign_for_year: false
      });
    }
  }, [open]);

  const loadBranches = async () => {
    try {
      setLoadingData(true);
      const branchesData = await getLocations();
      // getLocations ya devuelve response.data.data, así que es un array directo
      setBranches(branchesData || []);
    } catch (error) {
      console.error('Error loading branches:', error);
      SnackbarUtils.error(t('error_loading_branches') || 'Error al cargar sucursales');
    } finally {
      setLoadingData(false);
    }
  };

  const loadStaff = async () => {
    try {
      setLoadingData(true);
      const staffData = await getStaff();
      setStaff(staffData || []);
    } catch (error) {
      console.error('Error loading staff:', error);
      SnackbarUtils.error(t('error_loading_staff') || 'Error al cargar usuarios');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    
    // Si cambia el scope, limpiar solo los campos que ya no son relevantes
    if (field === 'scope') {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        // Solo limpiar branch_id si el nuevo scope no es BRANCH
        ...(value !== SCOPE_OPTIONS.BRANCH && { branch_id: null }),
        // Solo limpiar user_id si el nuevo scope no es PERSONAL
        ...(value !== SCOPE_OPTIONS.PERSONAL && { user_id: null })
      }));
    } else {
      // Para otros campos, mantener todos los valores existentes
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSave = async () => {
    if (!formData.requirement_value) {
      SnackbarUtils.error(t('fill_required_fields') || 'Por favor completa todos los campos requeridos');
      return;
    }

    const requirementValue = parseInt(formData.requirement_value, 10);
    if (isNaN(requirementValue) || requirementValue <= 0) {
      SnackbarUtils.error(t('invalid_requirement_value') || 'El valor requerido debe ser un número entero mayor a 0');
      return;
    }

    if (formData.scope === SCOPE_OPTIONS.BRANCH && !formData.branch_id) {
      SnackbarUtils.error(t('select_branch') || 'Por favor selecciona una sucursal');
      return;
    }

    if (formData.scope === SCOPE_OPTIONS.PERSONAL && !formData.user_id) {
      SnackbarUtils.error(t('select_user') || 'Por favor selecciona un usuario');
      return;
    }

    // Validar que si es scope PERSONAL, el user_id esté presente y sea válido
    if (formData.scope === SCOPE_OPTIONS.PERSONAL) {
      console.log('[AssignAchievementModal] Validating user_id for PERSONAL scope:', formData.user_id);
      if (!formData.user_id || formData.user_id === null || formData.user_id === undefined) {
        SnackbarUtils.error(t('select_user') || 'Por favor selecciona un usuario');
        return;
      }
    }

    try {
      setLoading(true);
      setProgress(0);

      if (formData.assign_for_year) {
        // Asignar para todos los meses del año (un solo request con month: null o 0)
        console.log('[AssignAchievementModal] Starting yearly assignment. FormData:', formData);
        
        // Asegurar que user_id sea un string limpio si es scope PERSONAL
        let userId = null;
        if (formData.scope === SCOPE_OPTIONS.PERSONAL) {
          if (!formData.user_id) {
            throw new Error('user_id es requerido para scope PERSONAL');
          }
          userId = String(formData.user_id).trim();
          if (!userId) {
            throw new Error('user_id no puede estar vacío para scope PERSONAL');
          }
        }
        
        const assignmentData = {
          achievement_id: achievement.id,
          scope: formData.scope,
          branch_id: formData.scope === SCOPE_OPTIONS.BRANCH ? formData.branch_id : null,
          user_id: userId,
          month: null, // null o 0 indica que se crearán las 12 asignaciones del año
          year: parseInt(formData.year, 10),
          requirement_value: requirementValue
        };

        console.log('[AssignAchievementModal] Assigning for whole year:', assignmentData);
        console.log('[AssignAchievementModal] user_id value:', assignmentData.user_id, '(type:', typeof assignmentData.user_id, ', scope:', formData.scope, ')');

        // Simular progreso mientras se procesa
        setProgress(50);
        const response = await AchievementsService.assignAchievement(assignmentData);
        setProgress(100);
        SnackbarUtils.success(t('yearly_achievement_assigned') || 'Logro asignado para todo el año exitosamente');
        
        // Pasar la respuesta al callback para actualizar los datos
        onSave(response);
      } else {
        // Asignar para un mes específico
        // Asegurar que user_id sea un string limpio si es scope PERSONAL
        let userId = null;
        if (formData.scope === SCOPE_OPTIONS.PERSONAL) {
          if (!formData.user_id) {
            SnackbarUtils.error(t('select_user') || 'Por favor selecciona un usuario');
            setLoading(false);
            return;
          }
          userId = String(formData.user_id).trim();
          if (!userId) {
            SnackbarUtils.error(t('select_user') || 'Por favor selecciona un usuario');
            setLoading(false);
            return;
          }
        }
        
        const assignmentData = {
          achievement_id: achievement.id,
          scope: formData.scope,
          branch_id: formData.scope === SCOPE_OPTIONS.BRANCH ? formData.branch_id : null,
          user_id: userId,
          month: parseInt(formData.month, 10),
          year: parseInt(formData.year, 10),
          requirement_value: requirementValue
        };

        console.log('[AssignAchievementModal] Assignment Data to send:', assignmentData);
        console.log('[AssignAchievementModal] user_id value:', assignmentData.user_id, '(type:', typeof assignmentData.user_id, ', scope:', formData.scope, ')');
        const response = await AchievementsService.assignAchievement(assignmentData);
        SnackbarUtils.success(t('achievement_assigned') || 'Logro asignado exitosamente');
        
        // Pasar la respuesta al callback para actualizar los datos
        onSave(response);
      }
    } catch (error) {
      console.error('[AssignAchievementModal] Error assigning achievement:', error);
      console.error('[AssignAchievementModal] Error response:', error.response?.data);
      SnackbarUtils.error(t('error_assigning_achievement') || 'Error al asignar logro');
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  const getDefaultRequirementValue = () => {
    return achievement?.defaultRequirementValue ?? achievement?.default_requirement_value ?? achievement?.requirementValue ?? achievement?.requirement_value ?? achievement?.targetValue ?? achievement?.target_value ?? '';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('assign_achievement') || 'Asignar Logro'} - {achievement?.name}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
          {/* Información del logro */}
          <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('achievement_info') || 'Información del Logro'}
            </Typography>
            <Typography variant="body2">
              <strong>{t('name') || 'Nombre'}:</strong> {achievement?.name}
            </Typography>
            {achievement?.description && (
              <Typography variant="body2">
                <strong>{t('description') || 'Descripción'}:</strong> {achievement.description}
              </Typography>
            )}
            {getDefaultRequirementValue() && (
              <Typography variant="body2" color="text.secondary">
                <strong>{t('suggested_value') || 'Valor sugerido'}:</strong> {getDefaultRequirementValue().toLocaleString()} 
                <Typography component="span" variant="caption" sx={{ ml: 1, fontStyle: 'italic' }}>
                  ({t('suggested_value_note') || 'Se pre-llenará al asignar, pero puedes cambiarlo'})
                </Typography>
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Selector de alcance */}
          <FormControl component="fieldset">
            <FormLabel component="legend">
              {t('scope') || 'Alcance de la Asignación'}
            </FormLabel>
            <RadioGroup
              value={formData.scope}
              onChange={handleChange('scope')}
            >
              <FormControlLabel
                value={SCOPE_OPTIONS.GLOBAL}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">
                      🌍 {t('global') || 'Global'} - {t('global_description') || 'Aplica a toda la empresa'}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value={SCOPE_OPTIONS.BRANCH}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">
                      🏢 {t('branch') || 'Sucursal'} - {t('branch_description') || 'Aplica a una sucursal específica'}
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value={SCOPE_OPTIONS.PERSONAL}
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">
                      👤 {t('personal') || 'Personal'} - {t('personal_description') || 'Aplica a un usuario específico'}
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {/* Selector de sucursal (si scope = BRANCH) */}
          {formData.scope === SCOPE_OPTIONS.BRANCH && (
            <TextField
              select
              label={t('select_branch') || 'Seleccionar Sucursal'}
              value={formData.branch_id || ''}
              onChange={handleChange('branch_id')}
              fullWidth
              required
              disabled={loadingData}
            >
              {branches.map((branch) => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.name || branch.title || `Sucursal ${branch.id}`}
                </MenuItem>
              ))}
            </TextField>
          )}

          {/* Selector de usuario (si scope = PERSONAL) */}
          {formData.scope === SCOPE_OPTIONS.PERSONAL && (
            <Autocomplete
              options={staff}
              getOptionLabel={(option) => {
                if (!option) return '';
                const firstName = option.firstName || option.first_name || '';
                const lastName = option.lastName || option.last_name || '';
                const fullName = `${firstName} ${lastName}`.trim();
                return fullName ? `${fullName} (${option.email || ''})` : option.email || '';
              }}
              value={selectedUser}
              onChange={(event, newValue) => {
                console.log('[AssignAchievementModal] User selected:', newValue);
                setFormData(prev => {
                  if (newValue) {
                    // El ID del usuario es el campo 'ci' (cédula de identidad)
                    const userId = newValue.ci;
                    console.log('[AssignAchievementModal] Setting user_id to:', userId);
                    return {
                      ...prev,
                      user_id: userId
                    };
                  } else {
                    return {
                      ...prev,
                      user_id: null
                    };
                  }
                });
              }}
              loading={loadingData}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('select_user') || 'Seleccionar Usuario'}
                  required
                  disabled={loadingData}
                />
              )}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return option === value;
                // El ID del usuario es el campo 'ci' (cédula de identidad)
                return option.ci === value.ci;
              }}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                return options.filter(option => {
                  const firstName = (option.firstName || option.first_name || '').toLowerCase();
                  const lastName = (option.lastName || option.last_name || '').toLowerCase();
                  const fullName = `${firstName} ${lastName}`.trim();
                  const email = (option.email || '').toLowerCase();
                  const searchTerm = inputValue.toLowerCase();
                  return fullName.includes(searchTerm) || email.includes(searchTerm);
                });
              }}
              renderOption={(props, option) => {
                const firstName = option.firstName || option.first_name || '';
                const lastName = option.lastName || option.last_name || '';
                const fullName = `${firstName} ${lastName}`.trim();
                return (
                  <Box component="li" {...props} key={option.ci}>
                    {fullName || option.email} ({option.email})
                  </Box>
                );
              }}
            />
          )}

          {/* Opción para asignar todo el año */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.assign_for_year}
                onChange={handleChange('assign_for_year')}
              />
            }
            label={t('assign_for_year') || 'Asignar para todo el año'}
          />
          {formData.assign_for_year && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {t('yearly_assignment_info') || 'Se asignará el mismo valor requerido para todos los 12 meses del año seleccionado.'}
            </Alert>
          )}

          {/* Mes y Año */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select
              label={formData.assign_for_year ? t('year') || 'Año' : t('month') || 'Mes'}
              value={formData.month}
              onChange={handleChange('month')}
              fullWidth
              required
              disabled={formData.assign_for_year}
              helperText={formData.assign_for_year ? t('all_months_selected') || 'Se asignará para todos los meses' : ''}
            >
              {[
                { value: 1, label: t('january') || 'Enero' },
                { value: 2, label: t('february') || 'Febrero' },
                { value: 3, label: t('march') || 'Marzo' },
                { value: 4, label: t('april') || 'Abril' },
                { value: 5, label: t('may') || 'Mayo' },
                { value: 6, label: t('june') || 'Junio' },
                { value: 7, label: t('july') || 'Julio' },
                { value: 8, label: t('august') || 'Agosto' },
                { value: 9, label: t('september') || 'Septiembre' },
                { value: 10, label: t('october') || 'Octubre' },
                { value: 11, label: t('november') || 'Noviembre' },
                { value: 12, label: t('december') || 'Diciembre' }
              ].map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('year') || 'Año'}
              value={formData.year}
              onChange={handleChange('year')}
              fullWidth
              type="number"
              required
              inputProps={{ min: 2020, max: 2100 }}
            />
          </Box>

          {/* Barra de progreso cuando se asigna para todo el año */}
          {formData.assign_for_year && loading && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t('assigning_progress') || 'Asignando logros...'} {Math.round(progress)}%
              </Typography>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}

          {/* Valor requerido */}
          <TextField
            label={t('requirement_value') || 'Valor Requerido'}
            value={formData.requirement_value}
            onChange={handleChange('requirement_value')}
            fullWidth
            type="number"
            required
            inputProps={{ min: 0, step: 1 }}
            helperText={
              (() => {
                const defaultVal = getDefaultRequirementValue();
                if (defaultVal && formData.requirement_value !== defaultVal) {
                  return t('custom_value_info') || `Valor personalizado (valor sugerido: ${defaultVal.toLocaleString()})`;
                } else if (defaultVal) {
                  return `${t('suggested_value') || 'Valor sugerido'}: ${defaultVal.toLocaleString()}. ${t('can_change') || 'Puedes cambiarlo si lo necesitas.'}`;
                } else {
                  return t('requirement_value_help') || 'Ingresa el valor requerido para esta asignación';
                }
              })()
            }
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel') || 'Cancelar'}</Button>
        <LoadingButton onClick={handleSave} loading={loading} variant="contained">
          {t('assign') || 'Asignar'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AssignAchievementModal;

